import type { ComboboxData } from '@mantine/core';
import { flowRuleInputs } from '@dealstack/shared/lib/common/business/documentFlow';
import type {
	FlowRuleInput,
	FlowRuleInputKind,
	FlowRuleOutcome,
	FlowRules,
	FlowRuleScenario,
	FlowScenarioAnswer,
} from '@dealstack/shared/lib/common/business/documentFlow';
import {
	dataPointChoices,
	sameChoiceValue,
} from '@dealstack/shared/lib/documents/dataPointChoices';
import type { DataPointChoiceSource } from '@dealstack/shared/lib/documents/dataPointChoices';

import type { OutcomeBadge, SegmentOption } from './rules.types';

/** The control value meaning "leave the recipient's own answer alone". */
export const RECORDED = '__recorded';
const NOT_RECORDED = '__blank';

export const isEmptyScenario = (scenario: FlowRuleScenario): boolean =>
	Object.keys(scenario.dataPoints).length === 0 && Object.keys(scenario.cohorts).length === 0;

const without = <T>(record: Record<string, T>, key: string): Record<string, T> =>
	Object.fromEntries(Object.entries(record).filter(([entry]) => entry !== key));

/** The scenario with one data point's answer set, or cleared back to what is recorded. */
export const withScenarioAnswer = (
	scenario: FlowRuleScenario,
	dataPointPath: string,
	answer: FlowScenarioAnswer | null
): FlowRuleScenario => ({
	...scenario,
	dataPoints:
		answer === null
			? without(scenario.dataPoints, dataPointPath)
			: { ...scenario.dataPoints, [dataPointPath]: answer },
});

/** The scenario with one cohort's membership set, or cleared back to what is recorded. */
export const withScenarioCohort = (
	scenario: FlowRuleScenario,
	cohortId: string,
	inCohort: boolean | null
): FlowRuleScenario => ({
	...scenario,
	cohorts:
		inCohort === null
			? without(scenario.cohorts, cohortId)
			: { ...scenario.cohorts, [cohortId]: inCohort },
});

/** The segmented choices for each fixed-option control, after the leading "as recorded". */
export const SEGMENT_OPTIONS: Record<'yesNo' | 'filled' | 'cohort', SegmentOption[]> = {
	yesNo: [
		{ value: 'yes', label: 'Yes' },
		{ value: 'no', label: 'No' },
		{ value: 'notAnswered', label: 'Not answered' },
	],
	filled: [
		{ value: 'filled', label: 'Filled in' },
		{ value: 'blank', label: 'Blank' },
	],
	cohort: [
		{ value: 'in', label: 'In cohort' },
		{ value: 'out', label: 'Not in cohort' },
	],
};

/** What a data point's control shows for the scenario's answer. */
export const answerControlValue = (
	kind: FlowRuleInputKind,
	answer: FlowScenarioAnswer | undefined
): string => {
	if (answer === undefined) {
		return RECORDED;
	}
	if (kind === 'choice') {
		if (answer.state === 'value') {
			return answer.value;
		}
		return answer.state === 'blank' ? NOT_RECORDED : RECORDED;
	}
	const offered = SEGMENT_OPTIONS[kind].some((option) => option.value === answer.state);
	return offered ? answer.state : RECORDED;
};

/** The answer a data point's control value stands for; null puts the recorded value back. */
export const answerFromControl = (
	kind: FlowRuleInputKind,
	value: string | null
): FlowScenarioAnswer | null => {
	if (value === null || value === RECORDED) {
		return null;
	}
	if (kind === 'choice') {
		return value === NOT_RECORDED ? { state: 'blank' } : { state: 'value', value };
	}
	switch (value) {
		case 'yes':
		case 'no':
		case 'notAnswered':
		case 'filled':
		case 'blank':
			return { state: value };
		default:
			return null;
	}
};

export const cohortControlValue = (inCohort: boolean | undefined): string => {
	if (inCohort === undefined) {
		return RECORDED;
	}
	return inCohort ? 'in' : 'out';
};

export const cohortFromControl = (value: string): boolean | null => {
	if (value === 'in') {
		return true;
	}
	return value === 'out' ? false : null;
};

/** The values the rules reading this data point name, so the picker can put them first. */
export const valuesNamedByRules = (input: FlowRuleInput, rules: FlowRules): string[] =>
	input.ruleIds.flatMap((ruleId) => {
		const when = rules[ruleId]?.when;
		return when?.operator === 'isOneOf' || when?.operator === 'isNoneOf' ? when.values : [];
	});

/**
 * The answers the current rules' controls can show. Anything else — an answer for a rule since
 * deleted, or one a changed rule's control cannot express — would apply while its control read
 * "As recorded".
 */
export const scenarioForRules = (
	scenario: FlowRuleScenario,
	rules: FlowRules
): FlowRuleScenario => {
	const inputs = flowRuleInputs(rules);
	return {
		dataPoints: Object.fromEntries(
			Object.entries(scenario.dataPoints).filter(([path, answer]) => {
				const input = inputs.find(
					(candidate) => candidate.kind !== 'cohort' && candidate.dataPointPath === path
				);
				return (
					input !== undefined &&
					input.kind !== 'cohort' &&
					answerControlValue(input.kind, answer) !== RECORDED
				);
			})
		),
		cohorts: Object.fromEntries(
			Object.entries(scenario.cohorts).filter(([cohortId]) =>
				inputs.some((input) => input.kind === 'cohort' && input.cohortId === cohortId)
			)
		),
	};
};

/** The picker's options: recorded, not recorded, the rules' own values, then everything else. */
export const choiceOptions = (
	source: DataPointChoiceSource,
	named: string[],
	recordedLabel: string
): ComboboxData => {
	const all = dataPointChoices(source);
	const isNamed = (value: string): boolean =>
		named.some((namedValue) => sameChoiceValue(namedValue, value));
	const first = all.filter((choice) => isNamed(choice.value));
	const rest = all.filter((choice) => !isNamed(choice.value));
	return [
		{ value: RECORDED, label: recordedLabel },
		{ value: NOT_RECORDED, label: 'Not recorded' },
		...(first.length === 0 ? [] : [{ group: 'Named in the rules', items: first }]),
		{ group: 'All', items: rest },
	];
};

/** How one rule's outcome reads beside it. */
export const outcomeBadge = (outcome: FlowRuleOutcome): OutcomeBadge => {
	// Unknown covers a value the list does not recognise ("USA"), not only a missing one.
	const noAnswer = outcome.answer === 'unknown' ? ' (no recognised answer)' : '';
	switch (outcome.decision) {
		case 'shown':
			return { label: `Shown${noAnswer}`, color: 'green' };
		case 'hidden':
			return { label: `Hidden${noAnswer}`, color: 'gray' };
		case 'undecided':
			return { label: 'Shown, labelled', color: 'violet' };
	}
};
